import URI from 'urijs';
import AppConfig from '../../constants/AppConfig';
import UrlConst from '../../constants/UrlConst';
import apiClient from '../common/apiClient';
import {
    makeCredentialsOnlyDeleteRequestSettings,
    makeCredentialsOnlyGetRequestSettings,
    makeDeleteRequestSettings,
    makeFormContentPostRequestSettings,
    makeGetRequestSettings,
    makePatchRequestSettings,
    makePostRequestSettings,
    makePutRequestSettings,
    makeUrl,
} from '../common/utils';
import {
    IFilter,
    INewReport,
    INewSchedule,
    IReports,
    IReportSchedule,
    IReportsDefinition,
    IReportsType,
    IReportTemplate,
    IUpdateSchedule,
} from '../../state/ui/reportsSlice';

export const makeReportsUrl = (suffix: string, params = {}) => {
    const url = new URI(
        AppConfig.instance.getUrl(UrlConst.REPORTS_URL) + suffix
    );
    return makeUrl(url, params);
};

export const restGetReports = (queryParams?: IFilter): Promise<IReports> =>
    apiClient.send(makeGetRequestSettings(makeReportsUrl('', queryParams)));

export const restGetReportsDefinitions = (): Promise<IReportsDefinition[]> =>
    apiClient.send(
        makeCredentialsOnlyGetRequestSettings(makeReportsUrl('/definitions'))
    );

export const restGetReportsTypes = (): Promise<IReportsType[]> =>
    apiClient.send(
        makeCredentialsOnlyGetRequestSettings(makeReportsUrl('/types'))
    );

export const restGetTemplates = (
    reportTypeId: number
): Promise<IReportTemplate[]> =>
    apiClient.send(
        makeCredentialsOnlyGetRequestSettings(
            makeReportsUrl(`/definitions/${reportTypeId}/templates`)
        )
    );

export const restPostGenerateReport = (
    report: INewReport
): Promise<IReportsDefinition[]> =>
    apiClient.send(
        makeFormContentPostRequestSettings(makeReportsUrl(''), report)
    );

export const restDeleteReport = (id: string): Promise<null> =>
    apiClient.send(
        makeCredentialsOnlyDeleteRequestSettings(makeReportsUrl(`/${id}`))
    );

export const restGetReportSchedules = (): Promise<IReportSchedule[]> =>
    apiClient.send(makeGetRequestSettings(makeReportsUrl('/schedules')));

export const restPostCreateSchedule = (
    schedule: INewSchedule
): Promise<IReportsDefinition[]> =>
    apiClient.send(
        makePostRequestSettings(makeReportsUrl('/schedules'), schedule)
    );

export const restPutUpdateSchedule = (
    id: string,
    schedule: IUpdateSchedule
): Promise<IReportsDefinition[]> =>
    apiClient.send(
        makePutRequestSettings(makeReportsUrl(`/schedules/${id}`), schedule)
    );

export const restDeleteSchedule = (id: string): Promise<null> =>
    apiClient.send(
        makeDeleteRequestSettings(makeReportsUrl(`/schedules/${id}`))
    );

export interface IRestPutUserReportNewAssignment {
    name: string;
    typeId: number;
    templateId: number;
}

export interface IRestPutUserReportAssignments {
    added?: Array<IRestPutUserReportNewAssignment>;
    updated?: Array<IRestPutUserReportNewAssignment & { assignmentId: number }>;
    removed?: Array<{
        assignmentId: number;
    }>;
}

export const restPatchUserReportAssignments = (
    userId: number,
    assignments: IRestPutUserReportAssignments
) =>
    apiClient.send(
        makePatchRequestSettings(makeReportsUrl('/definitions/assignments'), {
            userId,
            ...assignments,
        })
    );

export interface IRestGetUserReportAssignment {
    assignmentId: number;
    name: string;
    typeId: number;
    templateId: number;
}

export const restGetUserReportAssignments = async (
    userId: number
): Promise<IRestGetUserReportAssignment[]> => {
    const result = await apiClient.send(
        makeCredentialsOnlyGetRequestSettings(
            makeReportsUrl('/definitions/assignments', { userId })
        )
    );
    return result.reports;
};
